import { Component, DestroyRef, inject } from '@angular/core';
import { CreateCaComponent } from '../components/create-ca/create-ca.component';
import { CaRequestDto } from 'src/app/shared/services/api/dto/ca-request-dto';
import { PageService } from 'src/app/shared/page/service/page.service';
import { Page } from 'src/app/shared/page/page.config';
import { ToastrService } from 'src/app/shared/services/toastr/toastr-service';
import { LangService } from 'src/app/shared/services/lang/lang.service';
import { ApiService } from 'src/app/shared/services/api/api.service';
import { catchError, finalize, map, Observable, of, tap } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { SpinnerComponent } from 'src/app/shared/components/spinner/spinner.component';
import { extractErrorMessage } from 'src/app/shared/util/extract-error-message';
import { Profile } from 'src/app/shared/entities/profile';

@Component({
  selector: 'app-create-ca-page',
  imports: [CreateCaComponent, SpinnerComponent],
  templateUrl: './create-ca-page.component.html',
  styleUrl: './create-ca-page.component.scss',
})
export class CreateCaPageComponent {
  private toastrService = inject(ToastrService);
  private destroyRef = inject(DestroyRef);
  private pageService = inject(PageService);
  private apiService = inject(ApiService);
  private labels = inject(LangService).lang.features.calist.create;

  cryptotokenKeys?: { token: string; keys: string[] };
  loadingCryptotokenKeys = false;

  isCreating = false;

  onCreate(dto: CaRequestDto) {
    this.isCreating = true;
    this.apiService
      .createCa(dto)
      .pipe(
        tap(() => {
          this.onSuccess();
        }),
        catchError(error => {
          this.onFail(error);
          return of();
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isCreating = false;
        }),
      )
      .subscribe();
  }

  onCancel() {
    this.pageService.goToPage(Page.CaList);
  }

  private onSuccess() {
    this.toastrService.success(this.labels.toasts.created);
    this.pageService.goToPage(Page.CaList);
    // The CaList page takes care of reloading the list, nothing to be done here
  }

  private onFail(error: unknown) {
    console.error('CA creation error', error);
    const toast = extractErrorMessage(error) || this.labels.toasts.fail;
    this.toastrService.error(toast);
  }

  private cryptotokens$: Observable<{ loading: boolean; list: string[] }> =
    this.apiService.getCryptotokenList().pipe(
      map(tokens => ({
        loading: false,
        list: tokens.flatMap(token =>
          token.tokenName ? [token.tokenName] : [],
        ),
      })),
      catchError(error => {
        console.error('Error fetching crypto tokens', error);
        this.toastrService.error(
          extractErrorMessage(error) ||
            this.labels.toasts.couldNotFetchCryptoTokens,
        );
        return of({ loading: false, list: [] });
      }),
    );

  cryptotokens = toSignal(this.cryptotokens$, {
    initialValue: { loading: true, list: [] },
  });

  private profiles$: Observable<{ loading: boolean; list: Profile[] }> =
    this.apiService.getProfileList().pipe(
      map(profiles => ({
        loading: false,
        list: profiles.flatMap(profile =>
          profile.profileName ? [profile] : [],
        ),
      })),
      catchError(error => {
        console.error('Error fetching profiles', error);
        this.toastrService.error(
          extractErrorMessage(error) ||
            this.labels.toasts.couldNotFetchProfiles,
        );
        return of({ loading: false, list: [] });
      }),
    );

  profiles = toSignal(this.profiles$, {
    initialValue: { loading: true, list: [] },
  });

  private caNames$: Observable<{ loading: boolean; list: string[] }> =
    this.apiService.getCaList().pipe(
      map(profiles => ({
        loading: false,
        list: profiles.flatMap(ca => (ca.caName ? ca.caName : [])),
      })),
      catchError(error => {
        console.error('Error fetching CAs', error);
        this.toastrService.error(
          extractErrorMessage(error) ||
            this.labels.toasts.couldNotFetchProfiles,
        );
        return of({ loading: false, list: [] });
      }),
    );

  caNames = toSignal(this.caNames$, {
    initialValue: { loading: true, list: [] },
  });

  onNeedCryptotokenKeys({ tokenName }: { tokenName: string }) {
    this.loadingCryptotokenKeys = true;
    this.apiService
      .getCryptotokenKeys(tokenName)
      .pipe(
        tap(keys => {
          this.loadingCryptotokenKeys = false;
          this.cryptotokenKeys = {
            token: tokenName,
            keys: keys.flatMap(ctKey => {
              const alias = ctKey.details.alias;
              return alias ? [alias] : [];
            }),
          };
        }),
        catchError(error => {
          this.loadingCryptotokenKeys = false;
          this.toastrService.error(
            extractErrorMessage(error) ||
              this.labels.toasts.couldNotFetchCryptoTokenKeys,
          );
          return of();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
